import { RcFile } from 'antd/es/upload';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { stringify } from 'qs';
import { UploadRequestOption as RcCustomRequestOptions } from 'rc-upload/lib/interface';

import { ENDPOINTS } from 'config/endpoints';
import { getApiUrl } from 'config/urls';
import { ApiRequest } from 'stores/common';
import { ILocalStore } from 'stores/interfaces';
import { getHumanReadableFileSize } from 'utils/getHumanReadableFileSize';

import { BasicFormStore } from '../BasicFormStore';
import { FormStepStoreBaseParams } from '../types';

const FORM_DATA_FIELDS = {
  TABLE: 'table',
  FORM_ID: 'form_id',
};

export enum UploaderState {
  initial = 'initial',
  error = 'error',
  loading = 'loading',
  uploaded = 'uploaded',
}

type PrivateFields = '_fileData' | '_errorMessage' | '_uploadStartTime';

type UploadingFileData = {
  name: string;
  size: number;
};

const FILE_DEFAULT_NAME = 'Шаблон.xslx';

/** Стор второго этапа создания формы */
export class UploadFormStore implements ILocalStore {
  constructor({ basicStore }: FormStepStoreBaseParams) {
    this._basicStore = basicStore;

    makeObservable<UploadFormStore, PrivateFields>(this, {
      uploaderState: computed,
      upload: action.bound,

      _fileData: observable.ref,
      uploadingFileData: computed,

      _errorMessage: observable,
      errorMessage: computed,

      _uploadStartTime: observable,
      uploadStartTime: computed,

      enabledContinueButton: computed,

      downloadButtonParams: computed,
    });
  }

  private readonly _basicStore: BasicFormStore;

  private _fileData: UploadingFileData | null = null;

  private _errorMessage: string | null = null;

  private _uploadStartTime: number | null = null;

  /** Запрос на загрузку файла на бэк */
  private readonly _uploadRequest = new ApiRequest({
    methodType: 'POST',
    endpoint: ENDPOINTS.form.tableDataUpload,
    multipartFormData: true,
  });

  private get _id(): number {
    return this._basicStore.id || 0;
  }

  /** Информация о файле, который мы аплоадим на бэк*/
  get uploadingFileData() {
    if (!this._fileData) {
      return null;
    }

    return {
      ...this._fileData,
      size: getHumanReadableFileSize(this._fileData.size),
    };
  }

  get downloadButtonParams(): { href: string; download: string } {
    return {
      href: `${getApiUrl()}${ENDPOINTS.form.export}?${stringify({
        id: this._basicStore.id || 0,
      })}`,
      download: FILE_DEFAULT_NAME,
    };
  }

  /** Текущее состояние аплоадера для отображения правильного компонента*/
  get uploaderState(): UploaderState {
    if (this._uploadRequest.error) {
      return UploaderState.error;
    }

    if (this._uploadRequest.success) {
      return UploaderState.uploaded;
    }

    if (this._uploadRequest.loading) {
      return UploaderState.loading;
    }

    return UploaderState.initial;
  }

  get enabledContinueButton(): boolean {
    return this._uploadRequest.success;
  }

  /** Сообщение об ошибке с бэкенда */
  get errorMessage(): string | null {
    return this._errorMessage;
  }

  /** Время начала загрузки в миллисекундах */
  get uploadStartTime(): number | null {
    return this._uploadStartTime;
  }

  async upload(request: RcCustomRequestOptions | null): Promise<void> {
    if (!request?.file) {
      return;
    }

    runInAction(() => {
      this._uploadStartTime = Date.now();
    });

    const fd = new FormData();

    fd.append(FORM_DATA_FIELDS.TABLE, request.file);
    fd.append(FORM_DATA_FIELDS.FORM_ID, String(this._id));

    const response = await this._uploadRequest.call({
      data: fd,
      multipartFormData: true,
    });

    runInAction(() => {
      if (response.isError) {
        this._errorMessage = response.message || null;
      } else {
        this._errorMessage = null;
      }
      this._uploadStartTime = null;
    });

    if (response.isError) {
      return;
    }

    runInAction(() => {
      this._fileData = {
        name: request.filename || '',
        size: (request.file as RcFile).size || 0,
      };
    });
  }

  destroy(): void {
    this._uploadRequest.destroy();
  }
}
